import React from 'react'
import { stringify } from 'query-string'

import CommentTabMessageRestricted from '../../components/CommentTabMessageRestricted'

import { useQueryParams, useUserSignedIn } from '@sketch/modules-common'
import { TooltipShortcut } from '@sketch/components'
import { shortcuts } from '@sketch/utils'

import {
  useAnnotationOverlayContext,
  usePlaceDraftAnnotation,
} from '../../hooks'
import { useStatus } from '@sketch-hq/sketch-web-renderer'

import { ReactComponent as ChatBubbleAddIcon } from '@sketch/icons/comment-chat-bubble-plus-17'
import { AddAnnotationButton } from './ArtboardAnnotations.styles'
import AnnotationListContainer from '../AnnotationListContainer'
import { usePreview } from '../../../shares/views/PreviewView/PreviewContext'

interface ArtboardAnnotationsPreviewProps {
  showAddAnnotationButton?: boolean
}

export const ArtboardAnnotationsPreview: React.FCC<
  ArtboardAnnotationsPreviewProps
> = props => {
  const { showAddAnnotationButton } = props

  const canvasStatus = useStatus()
  const annotationContext = useAnnotationOverlayContext()
  const previewContext = usePreview()
  const isSignedIn = useUserSignedIn()

  const [isPlacingAnnotation, setPlaceAnnotation] =
    usePlaceDraftAnnotation() || []

  const { annotation, ...otherParams } = useQueryParams<
    'ARTBOARD_DETAIL_UUID' | 'ARTBOARD_DETAIL' | 'FRAME'
  >()

  const preview = previewContext?.preview

  if (!preview?.commentsEnabled || (previewContext && !isSignedIn)) {
    return <CommentTabMessageRestricted />
  }

  const isCanvasLoading = canvasStatus?.type !== 'READY'

  return (
    <AnnotationListContainer
      isCanvasLoading={isCanvasLoading}
      resolveAnnotationLink={annotation => location => ({
        ...location,
        state: {
          isFromCommentTab: true,
        },
        search: stringify({
          ...otherParams,
          annotation: annotation.identifier,
        }),
      })}
      navbarAction={
        <>
          {showAddAnnotationButton && (
            <AddAnnotationButton
              onClick={(event: MouseEvent) => {
                setPlaceAnnotation?.(!isPlacingAnnotation)
                event.stopPropagation()
              }}
              aria-current={isPlacingAnnotation}
              disabled={
                isCanvasLoading || !annotationContext?.isViewingLatestVersion
              }
              description={
                <>
                  Add comment{' '}
                  <TooltipShortcut>
                    Press {shortcuts.addComment.toUpperCase()}
                  </TooltipShortcut>
                </>
              }
            >
              <span className="sr-only">New comment</span>
              <ChatBubbleAddIcon role="img" />
            </AddAnnotationButton>
          )}
        </>
      }
      activeAnnotationIdentifier={annotation}
    />
  )
}
